import { Grid, Select, TextField } from '@customerio/pluma-components/react';
import { readInputValue } from '@/utils/dom';
import { isEmptyString } from '@/utils/string';
import {
  COUNTRY_PLACEHOLDER,
  COUNTRY_SELECT_OPTIONS,
  isUnitedStates,
  isZipCodeIgnored,
  US_STATE_PLACEHOLDER,
  US_STATE_SELECT_OPTIONS,
} from '../utils/address-options';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import type { BillingFieldErrors } from '../utils/credit-card-form-validation';

export type BillingAddressFieldsProps = {
  address: CreditCardFormAddress;
  canEdit: boolean;
  errors: BillingFieldErrors;
  onChange: (field: keyof CreditCardFormAddress, value: string) => void;
  onBlur: (field: string) => void;
};

/**
 * The billing-address inputs (address lines, city, country, zip/postal, and — for the US — state),
 * shared by the credit-card form and the credit-purchase form. A 4-column grid lays address line 1/2
 * and city/country two-up, with zip/state as quarter-width cells. The zip field is disabled and the
 * state select hidden where they don't apply.
 *
 * Every field carries `fs-mask`: a billing address is customer PII, so keep it out of FullStory.
 */
export function BillingAddressFields({ address, canEdit, errors, onChange, onBlur }: BillingAddressFieldsProps) {
  const showStateSelect = isUnitedStates(address.country);
  const zipDisabled = !canEdit || isZipCodeIgnored(address.country);

  return (
    <Grid
      areas={[
        'address-line-1 address-line-1 address-line-2 address-line-2',
        'city city country country',
        'row3-1 row3-2 row3-3 row3-4',
      ]}
      gap="300"
    >
      <TextField
        data-test-account-address-input="addressLine1"
        className="fs-mask"
        style={{ gridArea: 'address-line-1' }}
        label="Address line 1"
        isDisabled={!canEdit}
        value={address.addressLine1}
        error={errors.addressLine1}
        onInput={(event) => onChange('addressLine1', readInputValue(event))}
        onBlur={() => onBlur('addressLine1')}
      />
      <TextField
        data-test-account-address-input="addressLine2"
        className="fs-mask"
        style={{ gridArea: 'address-line-2' }}
        label="Address line 2"
        isDisabled={!canEdit}
        value={address.addressLine2}
        onInput={(event) => onChange('addressLine2', readInputValue(event))}
      />
      <TextField
        data-test-account-address-input="city"
        className="fs-mask"
        style={{ gridArea: 'city' }}
        label="City"
        isDisabled={!canEdit}
        value={address.city}
        error={errors.city}
        onInput={(event) => onChange('city', readInputValue(event))}
        onBlur={() => onBlur('city')}
      />
      <Select
        data-test-account-address-input="country"
        className="fs-mask"
        style={{ gridArea: 'country' }}
        label="Country"
        placeholder={COUNTRY_PLACEHOLDER}
        options={COUNTRY_SELECT_OPTIONS}
        value={isEmptyString(address.country) ? null : address.country}
        isDisabled={!canEdit}
        isSearchable={true}
        withVirtualizer={true}
        error={errors.country}
        onChange={(value) => {
          onChange('country', value ?? '');
          onBlur('country');
        }}
      />
      <TextField
        data-test-account-address-input="zipCode"
        className="fs-mask"
        style={{ gridArea: showStateSelect ? 'row3-3' : 'row3-1' }}
        label={showStateSelect ? 'Zip code' : 'Postal code'}
        isDisabled={zipDisabled}
        value={address.zipCode}
        error={errors.zipCode}
        onInput={(event) => onChange('zipCode', readInputValue(event))}
        onBlur={() => onBlur('zipCode')}
      />
      {showStateSelect ? (
        <Select
          data-test-account-address-input="state"
          className="fs-mask"
          style={{ gridArea: 'row3-1' }}
          label="State"
          placeholder={US_STATE_PLACEHOLDER}
          options={US_STATE_SELECT_OPTIONS}
          value={isEmptyString(address.state) ? null : address.state}
          isDisabled={!canEdit}
          error={errors.state}
          onChange={(value) => {
            onChange('state', value ?? '');
            onBlur('state');
          }}
        />
      ) : null}
    </Grid>
  );
}
